import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PatientSummaryTable from "../Oncall/PatientTable";
import { getPatientsByMrn } from "./patientApi";
import { supportHomePath, supportPatientPath } from "./supportUrls";

const SupportMrnPage = () => {
  const { mrn } = useParams();
  const navigate = useNavigate();
  const decodedMrn = decodeURIComponent(mrn || "");

  const [mrnResults, setMrnResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lookupError, setLookupError] = useState(null);

  useEffect(() => {
    if (!decodedMrn) {
      setLookupError("Please enter an MRN.");
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setLookupError(null);
      setMrnResults(null);
      try {
        const patients = await getPatientsByMrn(decodedMrn);
        if (cancelled) return;

        if (patients.length === 0) {
          setLookupError("No patients found for this MRN.");
        } else if (patients.length === 1) {
          navigate(
            supportPatientPath(patients[0].patient_id, { mrn: decodedMrn }),
            { replace: true }
          );
        } else {
          setMrnResults(patients);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [decodedMrn, navigate]);

  const handlePatientClick = (patientId) => {
    navigate(supportPatientPath(patientId, { mrn: decodedMrn }));
  };

  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto">
      <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>

      <Link
        to={supportHomePath()}
        className="mt-4 text-ktx-purple hover:underline font-medium text-sm w-fit"
      >
        Back to search
      </Link>

      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800">MRN search results</h2>
        <p className="mt-2 text-sm text-gray-600">
          MRN: <span className="font-medium text-gray-900">{decodedMrn}</span>
        </p>

        {isLoading && <p className="mt-4 text-sm text-gray-500">Searching...</p>}

        {lookupError && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {lookupError}
          </p>
        )}

        {!isLoading && mrnResults && mrnResults.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Multiple patients found — select one:
            </p>
            <PatientSummaryTable
              patients={mrnResults}
              onPatientIdClick={handlePatientClick}
              selectedPatientId={null}
              showDeleteColumn={false}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default SupportMrnPage;
